/**
 * Audit Log viewer.
 *
 * Read-only page that surfaces `audit_logs` entries for a project.
 * Writes come from `lib/audit.ts#logAudit()` sprinkled into mutating
 * routes (plugin install/uninstall, webhook create/delete, billing, etc.).
 *
 * Admins can filter by action, resource, and date range. Entries expire
 * automatically after 365 days via a TTL index on the collection.
 */
import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollText, Loader2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

interface AuditRow {
  id: string;
  userId: string;
  userEmail: string;
  action: string;
  resource: string;
  resourceId?: string;
  details?: Record<string, any>;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
}

const PAGE_SIZE = 50;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function AuditLogPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<string>('all');
  const [resource, setResource] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const facetsQuery = useQuery({
    queryKey: ['audit-facets', projectId],
    queryFn: async () => {
      const { data } = await api.get(`/audit/${projectId}/actions`);
      return data.data as { actions: string[]; resources: string[] };
    },
    enabled: Boolean(projectId),
  });

  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));
  if (action !== 'all') params.set('action', action);
  if (resource !== 'all') params.set('resource', resource);
  if (startDate) params.set('startDate', new Date(startDate).toISOString());
  if (endDate) params.set('endDate', new Date(endDate).toISOString());

  const logsQuery = useQuery({
    queryKey: ['audit-logs', projectId, params.toString()],
    queryFn: async () => {
      const { data } = await api.get(`/audit/${projectId}?${params.toString()}`);
      return data.data as {
        items: AuditRow[];
        page: number;
        limit: number;
        total: number;
        hasMore: boolean;
      };
    },
    enabled: Boolean(projectId),
  });

  const items = logsQuery.data?.items ?? [];
  const total = logsQuery.data?.total ?? 0;
  const hasMore = logsQuery.data?.hasMore ?? false;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const resetFilters = () => {
    setAction('all');
    setResource('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  return (
    <div className="container mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <ScrollText className="h-7 w-7" /> Audit Log
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Sensitive actions (plugin install/uninstall, webhook changes, billing updates) are
            logged here. Entries are retained for 365 days.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => logsQuery.refetch()}
          disabled={logsQuery.isFetching}
        >
          {logsQuery.isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
          <CardDescription>
            Narrow the log to a specific action, resource, or time window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <div className="space-y-1">
              <Label>Action</Label>
              <Select
                value={action}
                onValueChange={(v) => {
                  setAction(v);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {(facetsQuery.data?.actions ?? []).map((a) => (
                    <SelectItem key={a} value={a}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Resource</Label>
              <Select
                value={resource}
                onValueChange={(v) => {
                  setResource(v);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All resources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All resources</SelectItem>
                  {(facetsQuery.data?.resources ?? []).map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>From</Label>
              <Input
                type="datetime-local"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>To</Label>
              <Input
                type="datetime-local"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={resetFilters} className="w-full">
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Events{' '}
            {total > 0 && <span className="text-muted-foreground">({total.toLocaleString()})</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logsQuery.isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-muted-foreground py-12 text-center text-sm">
              No audit events match the current filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left text-xs uppercase tracking-wide">
                    <th className="pb-2 pr-4 font-medium">When</th>
                    <th className="pb-2 pr-4 font-medium">User</th>
                    <th className="pb-2 pr-4 font-medium">Action</th>
                    <th className="pb-2 pr-4 font-medium">Resource</th>
                    <th className="pb-2 pr-4 font-medium">IP</th>
                    <th className="pb-2 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const isOpen = expandedId === row.id;
                    const hasDetails = Boolean(row.details && Object.keys(row.details).length > 0);
                    return (
                      <Fragment key={row.id}>
                        <tr className="border-b last:border-b-0">
                          <td className="text-muted-foreground py-2 pr-4 font-mono text-xs">
                            {formatDate(row.createdAt)}
                          </td>
                          <td className="py-2 pr-4">
                            {row.userEmail || <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="py-2 pr-4">
                            <Badge variant="secondary" className="font-mono text-xs">
                              {row.action}
                            </Badge>
                          </td>
                          <td className="py-2 pr-4">
                            <span className="font-mono text-xs">
                              {row.resource}
                              {row.resourceId ? (
                                <span className="text-muted-foreground">
                                  :{row.resourceId.slice(0, 10)}
                                </span>
                              ) : null}
                            </span>
                          </td>
                          <td className="text-muted-foreground py-2 pr-4 font-mono text-xs">
                            {row.ipAddress || '—'}
                          </td>
                          <td className="py-2">
                            {hasDetails ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedId(isOpen ? null : row.id)}
                              >
                                {isOpen ? 'Hide' : 'Show'}
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && hasDetails && (
                          <tr className="bg-muted/30 border-b">
                            <td colSpan={6} className="p-3">
                              <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs">
                                {JSON.stringify(row.details, null, 2)}
                              </pre>
                              {row.userAgent && (
                                <p className="text-muted-foreground mt-2 text-xs">
                                  <span className="font-medium">User agent:</span> {row.userAgent}
                                </p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="text-muted-foreground mt-4 flex items-center justify-between text-sm">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" /> Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasMore}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
